import { Box } from '@mui/material'
import { MaterialReactTable } from 'material-react-table'
import { useCallback, useEffect, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { muiRowDragHandleProps, TableToolbar } from '@/components'
import { useMrtKeyboardNavigation, useTable, useTableHandlers } from '@/hooks'
import { useDetailTabContext } from '@/pages/User/pages/Home/components/DetailTab'
import { SourceItem } from '@/rtkApi/mddApiSliceGeneratedV2'
import { createEmptySourceItem } from '@/utils/source/createEmptySourceItem'

import { useSourceColumnsV2 } from './useSourceColumnsV2'

type SourceTableV2Props = {
	onChange: (value: { sourceItems: SourceItem[] }) => void,
	onSave?: () => void,
	sourceItems: SourceItem[],
}

export const SourceTableV2 = ({ sourceItems, onChange, onSave }: SourceTableV2Props) => {
	const {
		state: { editMode },
	} = useDetailTabContext()
	const containerRef = useRef<HTMLDivElement>(null)

	// Handle data changes from table
	const handleDataChange = useCallback(
		(data: SourceItem[]) => {
			onChange({ sourceItems: data })
		},
		[onChange],
	)

	const {
		isUserInteracting,
		setSelectedRow,
		setTableData,
		tableData,
		updateTableData,
		deleteRow,
		updateCellValue,
	} = useTableHandlers<SourceItem>({
		initialData: sourceItems || [],
		// V2 callback mode instead of Redux
		onDataChange: handleDataChange,
	})

	const columns = useSourceColumnsV2({ updateCellValue })

	// Handler to insert a new row at a specific index
	const handleInsertRow = useCallback(
		(rowIndex: number) => {
			const newRow: SourceItem = {
				...createEmptySourceItem(),
				id: Date.now(),
				uuid: uuidv4(),
			}

			// updateTableData uses Immer, so mutate draft directly
			// rowIndex of -1 means insert at beginning (empty table case)
			const insertIndex = Math.max(0, rowIndex + 1)
			updateTableData((draft) => {
				draft.splice(insertIndex, 0, newRow)
			})
		},
		[updateTableData],
	)

	const table = useTable<SourceItem>({
		columns,
		data: tableData,
		deleteRow,
		editMode,
		onInsertRow: handleInsertRow,
		tableId: 'view-v2-sources',
		options: {
			enableEditing: true,
			editDisplayMode: 'table',
			muiTablePaperProps: {
				elevation: 3,
				variant: 'elevation',
				sx: {
					padding: '1rem 0.5rem',
					boxShadow: '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)',
				},
			},
			renderBottomToolbar: () => (
				<TableToolbar<SourceItem>
					updateTableData={updateTableData}
					newRowPayload={createEmptySourceItem()}
					tableData={tableData}
					setSelectedRow={setSelectedRow}
				/>
			),
			muiRowDragHandleProps: ({ table }) =>
				muiRowDragHandleProps({ updateTableData, table, editMode }),
		},
	})

	// Keyboard navigation
	useMrtKeyboardNavigation({
		table,
		editMode,
		onInsertRow: handleInsertRow,
		deleteRow,
		containerRef,
		onSave,
	})

	// Sync from parent when data is loaded or structure changes (add/remove row).
	// In edit mode, when length is unchanged we do not overwrite — reorder is done in table
	// and onDataChange updates parent; overwriting here with stale sourceItems would undo reorder.
	useEffect(() => {
		// No placeholder row: parent [] means "no sources" in data and in UI. User adds via "New" in the toolbar
		// (a synthetic row here was not synced to onChange and looked like a "stuck" row after deleting all).
		if (!sourceItems || sourceItems.length === 0) {
			setTableData([])
			return
		}
		if (!editMode) {
			setTableData(sourceItems)
			return
		}
		// Edit mode: only sync when length changed (row added/removed), not on reorder
		setTableData((current) =>
			current.length !== sourceItems.length ? sourceItems : current,
		)
	}, [sourceItems, editMode, setTableData])

	// Set isUserInteracting on mount to ensure selects work properly
	// (react-select renders menu in portal outside our container)
	useEffect(() => {
		isUserInteracting.current = true
	}, [isUserInteracting])

	return (
		<Box
			ref={containerRef}
			sx={{ width: '100%' }}
			tabIndex={0}
		>
			<MaterialReactTable table={table} />
		</Box>
	)
}
